import { object, string, array } from 'yup';
import { BaseModel, CreateBaseModel } from './base.model';
import { isValidEmail, JsonObject, UiOption } from '../helpers';
import * as yup from 'yup';

export type OrgUser = {
  name: string;
  email: string;
};

export type OrgStatus = 'active' | 'inactive' | 'error' | 'creating' | 'deleting';

export type OrgMode = 'trial' | 'platform' | 'demo';

export const OrgModeOptions: UiOption[] = [
  { label: 'Trial', value: 'trial' },
  { label: 'Platform', value: 'platform' },
  { label: 'Demo', value: 'demo' },
];

export type PolicyEngineVersion = 'v1' | 'v2';

export type PolicyEngineMergeStrategy = 'least-restrictive' | 'most-restrictive';

export type OrgPolicyEngine = {
  version: PolicyEngineVersion;
  mergeStrategy: PolicyEngineMergeStrategy;
};

export const PolicyEngineMergeStrategyOptions: UiOption[] = [
  { label: 'Least restrictive', value: 'least-restrictive' },
  { label: 'Most restrictive', value: 'most-restrictive' },
];

export const DefaultOrgPolicyEngine: OrgPolicyEngine = {
  version: 'v1',
  mergeStrategy: 'least-restrictive',
};

export const OrgStatusOptions: UiOption[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

export class OrgModel extends BaseModel {
  partnerId: string;
  name: string;
  domains: string[];
  contact: OrgUser;
  adminUser?: OrgUser;
  allowSupportAccess: boolean;
  status: OrgStatus;
  mode: OrgMode;
  startDate: string;
  term: number;
  disablePromptRetention: boolean;
  policyEngine: OrgPolicyEngine;

  schema = object({
    name: string().required('Organization name is required'),
    domains: array().of(yup.string()).min(1, 'Your organization must have at least one domain'),
    partnerId: string().required('Partner is required'),
    contact: object({
      name: string().required('Contact name is required'),
      email: string()
        .test('email', 'Please enter a valid email address', isValidEmail)
        .required('Contact email is required'),
    }),
  });

  // On create only: a demo org needs the sign-in password for its admin. Same rule as the API.
  get createSchema() {
    return this.schema.concat(
      object({
        demoAdminPassword: string().when('mode', {
          is: 'demo',
          then: (schema) =>
            schema
              .required('A demo admin password is required')
              .min(12, 'Password must be at least 12 characters')
              .max(128, 'Password cannot exceed 128 characters')
              .matches(/[a-z]/, 'Password must include a lowercase letter')
              .matches(/[A-Z]/, 'Password must include an uppercase letter')
              .matches(/[0-9]/, 'Password must include a digit')
              .matches(/[^A-Za-z0-9]/, 'Password must include a symbol'),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    );
  }

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof OrgModel, unknown>;

    this.partnerId = (data.partnerId as string) || '';

    this.name = (data.name as string) || '';

    this.domains = (data.domains as string[]) || [];

    this.contact = (data.contact as OrgUser) || { name: '', email: '' };
    this.adminUser = data.adminUser as OrgUser | undefined;

    this.status = (data.status as OrgStatus) || 'inactive';

    this.mode = (data.mode as OrgMode) || 'platform';

    this.startDate = (data.startDate as string) || '';

    this.term = (data.term as number) || 0;

    this.disablePromptRetention = !!data.disablePromptRetention;

    this.allowSupportAccess = Boolean(data.allowSupportAccess);

    this.policyEngine = {
      ...DefaultOrgPolicyEngine,
      ...((data.policyEngine as Partial<OrgPolicyEngine>) || {}),
    };
  }
}

export const DemoAdminPasswordRule =
  '12 to 128 characters with a lowercase letter, an uppercase letter, a digit and a symbol';

export type OrgCreateFormValues = OrgModel & {
  domain?: string;
  createAdminUser?: boolean;
  demoAdminPassword?: string;
};

export class CreateOrgModel extends CreateBaseModel {
  partnerId: string;
  name: string;
  domains: string[];
  contact?: OrgUser;
  adminUser?: OrgUser;
  mode: OrgMode;
  startDate: string;
  term: number;
  disablePromptRetention?: boolean;
  // Sent on create for a demo org only. Never stored by the API.
  demoAdminPassword?: string;

  status?: OrgStatus;
  allowSupportAccess?: boolean;

  constructor(org: OrgCreateFormValues, create = false, isSuper = false) {
    super();

    if (!create) {
      this.allowSupportAccess = org.allowSupportAccess;

      if (['active', 'inactive'].includes(org.status)) {
        this.status = org.status;
      }
    }

    this.partnerId = org.partnerId;
    this.name = org.name;
    this.domains = org.domains;
    this.contact = org.contact || { name: '', email: '' };
    this.mode = org.mode;
    this.startDate = org.startDate;
    this.term = org.term;

    this.disablePromptRetention = org.disablePromptRetention;

    if (!isSuper) {
      delete this.disablePromptRetention;
    }

    // A demo org's contact is always its owner: the demo admin password is set on that account.
    if ((org.createAdminUser || (create && org.mode === 'demo')) && this.contact) {
      this.adminUser = { ...this.contact };
    } else {
      delete this.adminUser;
    }

    if (create && org.mode === 'demo' && org.demoAdminPassword) {
      this.demoAdminPassword = org.demoAdminPassword;
    } else {
      delete this.demoAdminPassword;
    }
  }
}
